import * as React from "react"
import ReactDOM from "react-dom"
import { type IntervalOrigin } from "@digits-graphql/frontend/graphql-bearer"
import { findActivePath } from "@digits-shared/components/Router/useRoutePathAndMatch"
import { SvgBankDollar } from "@digits-shared/components/SVGIcons/line/BankDollar.svg"
import { SvgMoneyMove } from "@digits-shared/components/SVGIcons/line/MoneyMove.svg"
import { HeaderSubNavDivider } from "@digits-shared/DesignSystem/Containers/Headers/HeaderSubNav"
import { HeaderToggles } from "@digits-shared/DesignSystem/Containers/Headers/HeaderToggles"
import { ToggleLinkOption } from "@digits-shared/DesignSystem/FormElements/ToggleButtons"
import { DigitsLinkButton } from "@digits-shared/DesignSystem/LinkButton"
import useSession from "@digits-shared/hooks/useSession"
import { useWaitForElement } from "@digits-shared/hooks/useWaitForElement"
import {
  AnalysisHeader,
  AnalysisToggle,
} from "src/frontend/components/OS/Applications/Analysis/AnalysisPageHeader"
import { Desktop } from "src/frontend/components/Shared/Responsive/Desktop"
import { Mobile } from "src/frontend/components/Shared/Responsive/Mobile"
import { useFrontendPathAndMatch } from "src/frontend/hooks/useFrontendPathAndMatch"
import { useFrontendPathGenerator } from "src/frontend/hooks/useFrontendPathGenerator"
import routes from "src/frontend/routes"
import type FrontendSession from "src/frontend/session"
import { PeriodSelectorNav } from "src/shared/components/PeriodSelector/PeriodSelectorNav"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

export const RevenueHeader: React.FC = () => {
  const session = useSession<FrontendSession>()
  const intervalOriginWithoutCount = {
    leSlug: session.currentLegalEntity.slug,
    ...useIntervalOrigin(),
  }
  delete intervalOriginWithoutCount.intervalCount
  const generatePath = useFrontendPathGenerator()

  return (
    <AnalysisHeader>
      <Mobile>
        <AnalysisToggle>
          <DigitsLinkButton
            to={generatePath(routes.expenses, intervalOriginWithoutCount)}
            size="medium"
            $variant="ghost-light"
          >
            <SvgMoneyMove />
            Expenses
          </DigitsLinkButton>
          <DigitsLinkButton
            to={generatePath(routes.revenue, intervalOriginWithoutCount)}
            $active
            size="medium"
            $variant="ghost-light"
          >
            <SvgBankDollar />
            Revenue
          </DigitsLinkButton>
        </AnalysisToggle>
      </Mobile>

      <Desktop>
        <PeriodSelectorNav />
        <RevenueToggle origin={intervalOriginWithoutCount} />
      </Desktop>
    </AnalysisHeader>
  )
}

const RevenueToggle: React.FC<{ origin: IntervalOrigin }> = ({ origin }) => {
  const root = useWaitForElement("analysis-toggle")

  const overviewMatch = useFrontendPathAndMatch(routes.revenue, origin)
  const categoriesMatch = useFrontendPathAndMatch(routes.revenueByCategories, origin)
  const customersMatch = useFrontendPathAndMatch(routes.revenueByCustomers, origin)
  const transactionsMatch = useFrontendPathAndMatch(routes.revenueTransactions, origin)
  const defaultValue = findActivePath(
    [transactionsMatch, customersMatch, categoriesMatch],
    overviewMatch
  )

  if (!root) return null

  return ReactDOM.createPortal(
    <>
      <HeaderSubNavDivider />
      <HeaderToggles defaultValue={defaultValue}>
        <ToggleLinkOption to={overviewMatch[0]}>Overview</ToggleLinkOption>
        <ToggleLinkOption to={categoriesMatch[0]}>Categories</ToggleLinkOption>
        <ToggleLinkOption to={customersMatch[0]}>Customers</ToggleLinkOption>
        <ToggleLinkOption to={transactionsMatch[0]}>Transactions</ToggleLinkOption>
      </HeaderToggles>
    </>,
    root
  )
}
